import { MAX_REDEEMABLE_POINTS_PER_ORDER } from 'constants/shop.js';

/**
 * Calculates perk-related data for a product card.
 *
 * @param {Object} params - The parameters object
 * @param {number} params.loyaltyPointsAvailable - Total loyalty points available, typically from user's account data
 * @param {number} params.loyaltyPointsBalance - Loyalty points remaining for the current order, less any points redeemed in the cart
 * @param {Object} params.productData - Product data object
 * @param {boolean} params.showMembershipPrice - Whether to show membership price
 * @param {string} params.variant - The product card variant
 * @example
 * const perksData = getPerksData({
 *   loyaltyPointsAvailable: 600,
 *   loyaltyPointsBalance: 500,
 *   productData: { plusMemberPointPrice: 100, pointPrice: 150 },
 *   showMembershipPrice: true,
 *   variant: 'PERK',
 * });
 *
 * @returns {Object} Object containing perk-related data
 * @returns {number} returns.pointPrice - A Plus Membership aware point price for the item
 * @returns {boolean} returns.isPerk - Whether the product is a perk
 * @returns {boolean} returns.isPerkRedeemable - Whether the perk is redeemable
 */
const getPerksData = ({ loyaltyPointsAvailable, loyaltyPointsBalance, productData, showMembershipPrice, variant }) => {
    if (!['PERK', 'PERK-REDEEMED'].includes(variant)) {
        return {
            pointPrice: 0, // Not used for non-perk variants
            isPerk: false,
            isPerkRedeemable: false,
        };
    }

    const pointPrice = showMembershipPrice ? productData.plusMemberPointPrice : productData.pointPrice;

    if (pointPrice <= 0) {
        return {
            pointPrice: 0,
            isPerk: false,
            isPerkRedeemable: false,
        };
    }

    const wouldExceedMaxRedeemablePoints =
        loyaltyPointsAvailable - loyaltyPointsBalance + pointPrice > MAX_REDEEMABLE_POINTS_PER_ORDER;
    const isPerkRedeemable = loyaltyPointsBalance >= pointPrice && !wouldExceedMaxRedeemablePoints;

    return {
        pointPrice,
        isPerk: true,
        isPerkRedeemable,
    };
};

export { getPerksData };
